import { Button } from 'admin/components/atoms/Buttons';
import Img from 'admin/components/atoms/Img';
import Input from 'admin/components/atoms/Input';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Modal from 'react-modal';
import styled from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const PreviewImg = styled(Img)`
  border: 5px solid #eee;
  max-width: 250px;
  max-height: 250px;
  object-fit: contain;
`;

const StyledModal = styled(Modal)`
  padding: 5%;
  background: rgba(0, 0, 0, 0.6);
`;

const ModalContent = styled.div`
  text-align: center;
  width: 50%;
  margin: auto;
  max-height: 80vh;
  overflow: scroll;
`;

const ImgUploader = ({ onChangeImg, initSrc, needCrop = false, accept, inputProps, ...rest }) => {
  const [upImg, setUpImg] = useState();
  const imgRef = useRef(null);
  const [crop, setCrop] = useState({ unit: '%', width: 50, aspect: 9 / 9 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [startCrop, setStartCrop] = useState(false);
  const imageUrl = useMemo(
    () =>
      typeof initSrc === 'string' && initSrc.length > 0
        ? initSrc
        : upImg
        ? URL.createObjectURL(upImg)
        : null,
    [initSrc, upImg]
  );
  const [cropedImg, setCropedImg] = useState();

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];

      if (!!accept) {
        const acceptedTypes = accept.split(',').map((type) => type.trim());
        const fileType = selectedFile.type;

        if (!acceptedTypes.includes(fileType)) {
          return;
        }
      }

      setUpImg(selectedFile);

      if (needCrop) {
        setStartCrop(true);
      }
    }
  };

  const handleCompleteCrop = () => {
    setUpImg(cropedImg);
    setStartCrop(false);
  };

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    if (upImg) {
      onChangeImg(upImg);
    }
  }, [upImg]);

  useEffect(() => {
    if (!completedCrop || !imgRef.current) {
      return;
    }

    const f = async () => {
      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const crop = completedCrop;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext('2d');
      const pixelRatio = window.devicePixelRatio;

      canvas.width = crop.width * pixelRatio;
      canvas.height = crop.height * pixelRatio;

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );

      const croped = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(Error('Canvas is empty'));
              return;
            }
            blob.lastModifiedDate = new Date();
            blob.name = upImg.name;
            resolve(blob);
          },
          'image/jpeg',
          1
        );
      });
      setCropedImg(croped);
    };
    f();
  }, [completedCrop]);

  return (
    <Wrapper {...rest}>
      {imageUrl && <PreviewImg src={imageUrl} />}
      <Input type="file" accept={!!accept ? accept : 'image/*'} onChange={onSelectFile} {...inputProps} />
      <StyledModal
        isOpen={startCrop}
        onRequestClose={() => setStartCrop(false)}
        closeTimeoutMS={200}
        ariaHideApp={false}
        shouldCloseOnOverlayClick={false}
      >
        <ModalContent>
          <ReactCrop
            src={imageUrl}
            onImageLoaded={onLoad}
            crop={crop}
            onChange={setCrop}
            onComplete={setCompletedCrop}
          />
          <div>
            <Button onClick={handleCompleteCrop}>完了</Button>
          </div>
        </ModalContent>
      </StyledModal>
    </Wrapper>
  );
};

export default styled(ImgUploader)``;
